import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Settings,
  Shapes,
  Dumbbell,
  ClipboardPen,
  Route,
  Users,
  HeartHandshake,
  Mail,
  Clock,
  Gavel,
  Trophy,
  Tv,
  Radio,
} from 'lucide-react';
import { useEvent } from '../../contexts/EventContext';
import { isCourseEvent } from '../../lib/eventTypes';
import type { StepKey } from '../../hooks/useEventOverview';

// Le menu d'un événement, rangé par MOMENT et non par objet (2026-09-23) :
// on prépare (catégories, épreuves, inscriptions), on gère des gens (équipes,
// bénévoles, messages), puis vient le jour J (planning, juges, résultats).
// Une seule définition : la barre latérale ET la palette ⌘K la lisent.

export interface NavItem {
  key: string;
  text: string;
  icon: React.ReactNode;
  path: string;
  /** Autres chemins qui rendent l'entrée active (ex. résultats). */
  match?: RegExp;
  dataTour?: string;
  /** Étape de la vue d'ensemble dont la pastille s'affiche sur l'entrée. */
  step?: StepKey;
}

export interface NavSection {
  key: 'overview' | 'prepare' | 'participants' | 'dayOf';
  label: string | null;
  items: NavItem[];
}

/** Le menu d'un événement donné. Pure : le tour guidé l'appelle au démarrage,
 *  avant que l'événement qu'il vient de sélectionner ne soit rendu. */
export function buildNavSections(
  selectedEvent: { id: string; event_type?: string | null },
  t: (key: string) => string,
): NavSection[] {
  const course = isCourseEvent(selectedEvent.event_type);
  const resultsKind = course ? 'race-results' : 'crossfit-results';

  const sections: NavSection[] = [
    {
      key: 'overview',
      label: null,
      items: [
        { key: 'overview', text: t('nav.overview'), icon: <LayoutDashboard size={18} />, path: '/admin/overview' },
        { key: 'settings', text: t('nav.settings'), icon: <Settings size={18} />, path: `/admin/events/${selectedEvent.id}/settings` },
      ],
    },
    {
      key: 'prepare',
      label: t('nav.sectionPrepare'),
      items: [
        { key: 'categories', text: t('nav.categories'), icon: <Shapes size={18} />, path: '/admin/categories', step: 'categories' },
        { key: 'wods', text: course ? t('nav.courses') : t('nav.wods'), icon: <Dumbbell size={18} />, path: '/admin/wods', match: /^\/admin\/wods(\/|$)/, step: 'wods' },
        ...(course ? [{ key: 'courseStations', text: t('nav.courseStations'), icon: <Route size={18} />, path: '/admin/course-stations' }] : []),
        { key: 'registrations', text: t('nav.registrations'), icon: <ClipboardPen size={18} />, path: '/admin/registrations', step: 'registrations' },
      ],
    },
    {
      key: 'participants',
      label: t('nav.sectionParticipants'),
      items: [
        { key: 'teams', text: t('nav.teams'), icon: <Users size={18} />, path: '/admin/teams' },
        { key: 'volunteers', text: t('nav.volunteers'), icon: <HeartHandshake size={18} />, path: '/admin/volunteers' },
        { key: 'messages', text: t('nav.messages'), icon: <Mail size={18} />, path: '/admin/messages' },
      ],
    },
    {
      key: 'dayOf',
      label: t('nav.sectionDayOf'),
      items: [
        { key: 'heats', text: course ? t('nav.waves') : t('nav.planning'), icon: <Clock size={18} />, path: '/admin/heats', step: 'planning' },
        // Piloter le jour J en scoring en ligne, sans l'Organizer (2026-11-03).
        ...(course ? [] : [{ key: 'dayOf', text: t('nav.dayOf'), icon: <Radio size={18} />, path: '/admin/day-of' }]),
        // Planning des juges : CrossFit. Le flux Course/HYROX® a son propre
        // écran « Juges » (répertoire + rôles + appareil) — deux outils.
        course
          ? { key: 'staff', text: t('nav.eventStaff'), icon: <Gavel size={18} />, path: '/admin/staff' }
          : { key: 'judgePlan', text: t('nav.judgePlan'), icon: <Gavel size={18} />, path: '/admin/judge-plan' },
        {
          key: 'results',
          text: t('nav.results'),
          icon: <Trophy size={18} />,
          path: `/admin/events/${selectedEvent.id}/${resultsKind}`,
          match: /^\/admin\/events\/[^/]+\/(scores|crossfit-results|race-results)$/,
          step: 'results',
        },
        ...(course ? [
          { key: 'display', text: t('nav.display'), icon: <Tv size={18} />, path: '/admin/display' },
        ] : []),
      ],
    },
  ];
  // Chaque entrée porte une ancre `nav-<clé>` : le tour guidé passe par toutes.
  return sections.map((section) => ({
    ...section,
    items: section.items.map((item) => ({ ...item, dataTour: `nav-${item.key}` })),
  }));
}

export function useNavSections(): NavSection[] {
  const { t } = useTranslation();
  const { selectedEvent } = useEvent();
  if (!selectedEvent) return [];
  return buildNavSections(selectedEvent, t);
}

export function isNavItemActive(item: NavItem, pathname: string): boolean {
  return pathname === item.path || (!!item.match && item.match.test(pathname));
}
